import React, {
    useContext, useCallback, useEffect
} from 'react';
import {
    cloneDeep, get, set, sortBy, uniqBy
} from 'lodash';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { BreakpointTrackerContext } from '@jutro/layout';
import { WizardPage, wizardProps } from 'e1p-portals-wizard-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import messages from '../CoveragePage.messages';
import metadata from '../CoveragePage.metadata.json5';

function generateClauseData(columnData) {
    return columnData.map(({ lob, code }) => {
        const completeCoveragePath = 'coverages.coverages';

        return {
            code,
            path: `${lob.path}.${completeCoveragePath}`,
            clauses: get(lob.data, completeCoveragePath.replace(/\.children/, ''))
        };
    });
}

function generateExclusionClauseData(columnData) {
    return columnData.map(({ lob, code }) => {
        const completeExclusionPath = 'exclusions';

        return {
            code,
            path: `${lob.path}.${completeExclusionPath}`,
            clauses: get(lob.data, completeExclusionPath.replace(/\.children/, ''))
        };
    });
}

function generateConditionClauseData(columnData) {
    return columnData.map(({ lob, code }) => {
        const completeConditionPath = 'conditions';

        return {
            code,
            path: `${lob.path}.${completeConditionPath}`,
            clauses: get(lob.data, completeConditionPath.replace(/\.children/, ''))
        };
    });
}

const generateColumnData = (rewriteVM) => {
    const lobOfferingPath = 'lobData.homeowners_EH.offerings';
    const quoteOfferingPath = 'quoteData.offeredQuotes';

    const lobOfferings = get(rewriteVM, `${lobOfferingPath}.value`);
    const quoteOfferings = get(rewriteVM, `${quoteOfferingPath}.value`);

    let filteredOfferings = cloneDeep(lobOfferings);

    if (filteredOfferings.length > 1) {
        filteredOfferings = filteredOfferings
            .filter((offering) => offering.branchCode !== 'CUSTOM');
    }

    const columnData = filteredOfferings.map((lobOffering, lobIndex) => {
        const quoteDataIndex = quoteOfferings.findIndex(
            (qdOffering) => qdOffering.branchCode === lobOffering.branchCode
        );
        const quoteData = quoteOfferings[quoteDataIndex];

        return {
            name: lobOffering.branchName,
            code: lobOffering.branchCode,
            quote: {
                path: `${quoteOfferingPath}.children[${quoteDataIndex}]`,
                data: quoteData
            },
            lob: {
                path: `${lobOfferingPath}.children[${lobIndex}]`,
                data: lobOffering
            },
            rewriteVM
        };
    });

    // }).filter(({ code }) => code !== 'CUSTOM');
    return sortBy(columnData, ['code']);
};


const getExclusionsUniqueID = (rewriteVM) => {
    // putting ID into an object as the Jutro table component expects an object
    const structureClauseTableData = (exclusion) => ({
        publicID: exclusion.publicID,
        coverageCategoryCode: exclusion.exclusionCategoryCode,
        uigroup: exclusion.uigroup
    });

    const offerings = get(rewriteVM, 'lobData.homeowners_EH.offerings.value');
    const exclusions = uniqBy(
        offerings.flatMap((offering) => offering.exclusions.map(structureClauseTableData)),
        'publicID'
    );

    return {
        exclusions
    };
};

const getConditionsUniqueID = (rewriteVM) => {
    // putting ID into an object as the Jutro table component expects an object
    const structureClauseTableData = (condition) => ({
        publicID: condition.publicID,
        coverageCategoryCode: condition.conditionCategoryCode,
        uigroup: condition.uigroup
    });

    const offerings = get(rewriteVM, 'lobData.homeowners_EH.offerings.value');
    const conditions = uniqBy(
        offerings.flatMap((offering) => offering.conditions.map(structureClauseTableData)),
        'publicID'
    );

    return {
        conditions
    };
};

const getCoveragesUniqueID = (rewriteVM) => {
    // putting ID into an object as the Jutro table component expects an object
    const structureClauseTableData = (coverage) => ({
        publicID: coverage.publicID,
        coverageCategoryCode: coverage.coverageCategoryCode,
        uigroup: coverage.uigroup
    });
    const offerings = get(rewriteVM, 'lobData.homeowners_EH.offerings.value');
    const offerringsWithCovsNotHidden = offerings.map((offerring) => {
        const offerringFormatted = { ...offerring};

        offerringFormatted.coverages.coverages = offerring.coverages
            .coverages.filter((coverage) => coverage.isHiddenInVersion !== true);

        return offerringFormatted;
    });
    const baseCoverages = uniqBy(
        offerringsWithCovsNotHidden.flatMap((offering) => offering
            .coverages.coverages.map(structureClauseTableData)),
        'publicID'
    );
    const additionalCoverages = uniqBy(
        offerringsWithCovsNotHidden.flatMap((offering) => offering
            .coverages.schedules.map(structureClauseTableData)),
        'publicID'
    );
    const sectionICoverages = uniqBy(
        offerringsWithCovsNotHidden.flatMap((offering) => offering.coverages.coverages
            .filter((coverage) => coverage.uigroup === 'Section I')
            .map(structureClauseTableData)),
        'publicID'
    );
    const sectionIICoverages = uniqBy(
        offerringsWithCovsNotHidden.flatMap((offering) => offering.coverages.coverages
            .filter((coverage) => coverage.uigroup === 'Section II')
            .map(structureClauseTableData)),
        'publicID'
    );
    const deductibles = uniqBy(
        offerringsWithCovsNotHidden.flatMap((offering) => offering.coverages.coverages
            .filter((coverage) => coverage.uigroup === 'Deductibles')
            .map(structureClauseTableData)),
        'publicID'
    );

    return {
        baseCoverages,
        additionalCoverages,
        sectionICoverages,
        sectionIICoverages,
        deductibles
    };
};


const generateTableData = (rewriteVM, columnData) => {
    const uniqueID = getCoveragesUniqueID(rewriteVM);

    return Object.keys(uniqueID).map((coverageType) => ({
        header: get(messages, coverageType),
        data: get(uniqueID, coverageType),
        tableContent: generateClauseData(columnData, coverageType)
    }));
};

const generateExclusionTableData = (rewriteVM, columnData) => {
    const uniqueID = getExclusionsUniqueID(rewriteVM);

    return Object.keys(uniqueID).map((coverageType) => ({
        header: get(messages, coverageType),
        data: get(uniqueID, coverageType),
        tableContent: generateExclusionClauseData(columnData, coverageType)
    }));
};
const generateConditionTableData = (rewriteVM, columnData) => {
    const uniqueID = getConditionsUniqueID(rewriteVM);

    return Object.keys(uniqueID).map((coverageType) => ({
        header: get(messages, coverageType),
        data: get(uniqueID, coverageType),
        tableContent: generateConditionClauseData(columnData, coverageType)
    }));
};

function QuotePage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const {
        onValidate
    } = useValidation('QuotePage');

    const viewModelService = useContext(ViewModelServiceContext);
    const {
        wizardData: rewriteVM,
        updateWizardData,
        viewOnly,
        jumpTo,
        steps
    } = props;
    const { authHeader } = useAuthentication();
    const isNewBusiness = get(rewriteVM, 'baseData.value.businessTransactionType_Ext') === 'NewBusiness';

    useEffect(() => {
        // displaying only one offering
        if (rewriteVM.value.lobData.homeowners_EH.offerings.length > 1) {
            rewriteVM.value.lobData.homeowners_EH.offerings = rewriteVM
                .value.lobData.homeowners_EH.offerings
                .filter((offering) => offering.branchCode !== 'CUSTOM');
        }

        if (rewriteVM.lobData.homeowners_EH.paperlessInd.value === undefined
            && get(
                rewriteVM, 'baseData.quoteSource_Ext.sourceType.value.code'
            ) === 'directentry') {
            set(rewriteVM, 'lobData.homeowners_EH.paperlessInd.value', true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePrint = () => {
        window.print();
    };

    const checkComparatorFlow = useCallback(() => {
        if (get(rewriteVM, 'baseData.quoteSource_Ext.sourceType')) {
            const isSourceTypeCompRater = get(
                rewriteVM, 'baseData.quoteSource_Ext.sourceType.value.code'
            ) === 'comprater';
            const quoteType = get(rewriteVM, 'quoteType_Ext.value.code');

            return (isSourceTypeCompRater && quoteType === 'Quick');
        }

        return false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buyNow = useCallback(
        async () => rewriteVM,
        [rewriteVM]
    );

    const columnData = generateColumnData(rewriteVM);

    const policyType = get(rewriteVM, 'lobData.homeowners_EH.policyType.value.code');

    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            readOnly: viewOnly
        },
        quotePageLoadingIndicator: {
            loaded: true
        },
        quotePageContainer: {
            visible: true
        },
        quoteProposalLinkContainer: {
            visible: isNewBusiness
        },
        quoteProposalLinkId: {
            disabled: true
        },
        quoteTableHO3: {
            columnData,
            tableData: generateTableData(rewriteVM, columnData),
            tableDataExclusion: generateExclusionTableData(rewriteVM, columnData),
            tableDataCondition: generateConditionTableData(rewriteVM, columnData),
            underwritingIssues: get(rewriteVM, 'errorsAndWarnings.underwritingIssues.value') === undefined
                ? [] : get(rewriteVM, 'errorsAndWarnings.underwritingIssues.value'),
            filterUWIssuesInCustomOffering: false,
            submissionVM: rewriteVM,
            modifiers: get(rewriteVM, 'lobData.homeowners_EH.modifiers.value'),
            isComprater: checkComparatorFlow(),
            visible: true,
            onDisableNextForTable: () => { },
            viewModelService,
            updateWizardData,
            authHeader,
            jumpTo,
            steps,
            setFieldsChangedOnCoveragePage: () => { },
            fieldsChangedOnCoveragePage: false,
            policyType,
            quoteIsStale: false,
            onScheduleChange: () => { },
            viewOnlyMode: true
        },
        ncrbDiscountAvailabilityMessageDiv: {
            visible: false
        },
        notificationContainer: {
            visible: false
        },
        quoteProposalFailureError: {
            visible: false
        },
        paperlessEmailChangedMessageDiv: {
            visible: false
        },
        quoteProposalFailureErrorDiv: {
            visible: false
        },
        protectiveDeviceRequireInfoMessageBasedOnCovADiv: {
            visible: false
        },
        windstormMitigationDiscountInfoMessageDiv: {
            visible: false
        }
    };

    return (
        <WizardPage
            shouldLink
        >
            {({ onNext }) => {
                const resolvers = {
                    resolveCallbackMap: {
                        onBuyNow: (lobPath, quotePath) => buyNow(lobPath, quotePath).then(onNext),
                        onRecalculate: () => { },
                        onResetQuote: () => { },
                        onChangeSubmissionAndSync: () => { },
                        onChangeSubmission: () => { },
                        onSyncCoverages: () => { },
                        onScheduleChange: () => { },
                        onStaleQuoteBranchCode: () => { },
                        onPrint: handlePrint,
                        updateWizardData
                    }
                };

                return (
                    <ViewModelForm
                        uiProps={metadata.pageContent}
                        model={rewriteVM}
                        overrideProps={overrideProps}
                        onModelChange={updateWizardData}
                        onValidationChange={onValidate}
                        callbackMap={resolvers.resolveCallbackMap}
                    />
                );
            }}
        </WizardPage>
    );
}

QuotePage.propTypes = wizardProps;
export default QuotePage;
